// Crafting shapes that cross the wire.
//
// CLAUDE.md: protocol shapes are defined once, here, and imported by both
// apps. These started life declared twice — once in the server's crafting
// module and once again in the bench panel — which is exactly how a field
// gets added on one side and silently ignored on the other.
//
// Only the shapes travel. How a look is DERIVED from resource stats is
// server-only and stays there: the client is handed a finished palette and
// paints it, and never gets to decide what a material looks like.

/**
 * How a flyer prints. Colours are `#rrggbb`; grain and register are words
 * for the paper and the ink, never numbers.
 */
export interface FlyerLook {
  /** Page colour, from the stock. */
  stock: string;
  /** Type colour, from the ink. */
  ink: string;
  /** Rule and border colour — where potency shows up. */
  accent: string;
  /** Paper character, in a word. */
  grain: string;
  /** How the ink sits, in a word. */
  register: string;
}

/** What the bench will make from a selection — and, once made, what it did. */
export interface FlyerPreview {
  title: string;
  nightTitle: string;
  clubId: string;
  look: FlyerLook;
  materials: Array<{ slot: string; spawnName: string; className: string; units: number }>;
}

/**
 * One quality of a made thing, in a word. "The voice is singing." The
 * integer behind the word lives in server-side provenance and never
 * crosses the wire — output quality is words and material description,
 * never numbers (crafting v2 decision 14).
 */
export interface AspectWord {
  id: string;
  /** "the voice", "the build" — always with the article, ready for prose. */
  label: string;
  word: string;
  /** Whether the crafter worked this aspect beyond the base assembly. */
  pressed: boolean;
}

/** A made thing, as anyone other than the database sees it. */
export interface ArtifactView {
  id: string;
  /** flyer | instrument | garment | furniture | drink. */
  artifactType: string;
  title: string;
  /** Resolved live from the maker's character row, never stored. */
  makerName: string;
  makerCharacterId: string;
  nightTitle: string;
  clubId: string;
  bookingId: string | null;
  look: FlyerLook;
  materials: Array<{
    slot: string;
    spawnName: string;
    className: string;
    stats: Record<string, number>;
  }>;
  /** Output quality, aspect by aspect, in words. Empty for a flyer. */
  aspects: AspectWord[];
  /**
   * The maker's mark read aloud — one server-composed sentence anybody
   * holding the thing can read: who made it, of what, how it came out.
   */
  legend: string;
  createdAt: string;
  /** True when the viewer is the one who made it. */
  mine: boolean;
  /** True when the viewer holds the object itself (not a keepsake copy). */
  owned: boolean;
  /** True when this row is a claimed keepsake copy, not the object. */
  keepsake: boolean;
  /** Who holds the object now (resolved live) — the maker's-eye view. */
  holderName: string;
  /** Garments: the body slot this can occupy ("coat", "scarf"). */
  wearSlot?: string;
  /** Garments: currently on the viewer's body. */
  equipped: boolean;
  /** Drinks: already drunk (kept as history on the maker's shelf). */
  consumed: boolean;
  /** Furniture: footprint metadata the housing wave places by. */
  footprint?: { w: number; d: number; h: number; placement: string };
  /**
   * Furniture: standing in a home right now. A placed piece cannot be
   * traded, discarded, or consumed until it is taken up again.
   */
  placed?: boolean;
}

/** A recipe slot as the bench panel reads it. */
export interface BenchSlotView {
  id: string;
  label: string;
  hint: string;
  units: number;
  options: Array<{
    spawnId: string;
    name: string;
    className: string;
    units: number;
    hold: number;
    locker: number;
    stats: Record<string, number>;
    enough: boolean;
  }>;
}

/** A recipe as the bench panel reads it. */
export interface BenchRecipeView {
  id: string;
  publicName: string;
  /** Which station in the workshop this work happens at. */
  station: string;
  stationLabel: string;
  /** One diegetic line about what this thing is for. */
  blurb: string;
  slots: BenchSlotView[];
  /** Aspects that can be worked (experimentation); empty for the flyer. */
  aspects: Array<{ id: string; label: string }>;
  /**
   * How many times one aspect will take being worked. Config for the
   * panel's buttons, never printed — the refusal surface is words.
   */
  maxPresses: number;
  /** Extra units each press costs, per slot feeding the aspect. */
  pressCost: Record<string, number>;
  /** Flyers only: the recipe needs a Night to advertise. */
  bindsNight: boolean;
  wearSlot?: string;
  consumable?: boolean;
}

/** What the bench would make of a full v2 selection — or did make. */
export interface CraftOutcomeView {
  recipeId: string;
  artifactType: string;
  title: string;
  nightTitle: string;
  clubId: string;
  look: FlyerLook;
  materials: Array<{ slot: string; spawnName: string; className: string; units: number }>;
  aspects: AspectWord[];
  /** One sentence about how the working went — strain reads here. */
  workNote: string;
}

/** The look a flyer falls back to if its provenance is ever unreadable. */
export const BRASS_LOOK: FlyerLook = {
  stock: "#e8dcc0",
  ink: "#2a2118",
  accent: "#d8b04a",
  grain: "laid",
  register: "even",
};

/**
 * A look, made safe to paint.
 *
 * The three colours go into a `style` attribute on the client, and
 * provenance is a jsonb column: the server used to hand back
 * `provenance.look ?? BRASS_LOOK` with no shape check at all, so anything
 * that ever managed to write that column — a future import, a fixture, an
 * admin tool, a bug in a recipe — owned a style attribute on the game
 * origin, next to a session cookie. There is no player write path into it
 * today, which is precisely why this is cheap to close now: the ONLY
 * thing standing between that column and script execution was the absence
 * of one.
 *
 * Colours must be `#rrggbb`; the two words are words. Anything else falls
 * back to brass, field by field, so an artifact with a half-broken
 * provenance still prints instead of vanishing.
 */
export function sanitizeFlyerLook(raw: unknown): FlyerLook {
  const look = (raw ?? {}) as Partial<Record<keyof FlyerLook, unknown>>;
  const hex = (value: unknown, fallback: string): string =>
    typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
  const word = (value: unknown, fallback: string): string =>
    typeof value === "string" && /^[a-z]+(?: [a-z]+)?$/.test(value) && value.length <= 24
      ? value
      : fallback;
  return {
    stock: hex(look.stock, BRASS_LOOK.stock),
    ink: hex(look.ink, BRASS_LOOK.ink),
    accent: hex(look.accent, BRASS_LOOK.accent),
    grain: word(look.grain, BRASS_LOOK.grain),
    register: word(look.register, BRASS_LOOK.register),
  };
}

/** A Night as the poster wall reads it — the /api/nights row. */
export interface NightBill {
  clubId: string;
  title: string;
  artistName: string;
  hostName: string;
  /** ISO, or null for a Night nobody has billed a time for yet. */
  scheduledFor: string | null;
  /** The flyer somebody printed for it, if anybody did. */
  flyer: { id: string; title: string; makerName: string; look: FlyerLook } | null;
}
